import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Snowflake, ExternalLink, AlertTriangle, KeyRound } from "lucide-react";

type FreezeResult = { ok: boolean; message: string; snapshotVersion?: number };
type DemoPinResult = { pin: string; source: "db" | "env" };

/**
 * DemoSandboxPanel — admin control for re-capturing the demo.padpulse.io
 * sandbox. The freeze re-reads the live source org (DEMO_SOURCE_ORG_ID),
 * anonymizes it, and loads it into the demo org. The source org's real rows
 * are never mutated. Gated server-side by requireAdmin on POST
 * /api/admin/demo/freeze.
 */
export function DemoSandboxPanel() {
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastVersion, setLastVersion] = useState<number | null>(null);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinInput, setPinInput] = useState("");

  const pinQuery = useQuery<DemoPinResult>({
    queryKey: ["/api/admin/demo/pin"],
  });

  const updatePin = useMutation({
    mutationFn: async (pin: string) => {
      const res = await apiRequest("PUT", "/api/admin/demo/pin", { pin });
      return (await res.json()) as DemoPinResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demo/pin"] });
      setPinDialogOpen(false);
      setPinInput("");
      toast({
        title: "Demo PIN updated",
        description: "The new PIN takes effect on the next demo login attempt.",
      });
    },
    onError: (err) => {
      toast({
        title: "Could not update PIN",
        description: parseErrorMessage(err, "Failed to update the demo PIN."),
        variant: "destructive",
      });
    },
  });

  const freeze = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/demo/freeze", {});
      return (await res.json()) as FreezeResult;
    },
    onSuccess: (data) => {
      setLastVersion(data.snapshotVersion ?? null);
      setConfirmOpen(false);
      toast({
        title: "Demo snapshot captured",
        description:
          data.snapshotVersion != null
            ? `Loaded into the demo org as snapshot v${data.snapshotVersion}.`
            : data.message,
      });
    },
    onError: (err) => {
      toast({
        title: "Freeze failed",
        description: parseErrorMessage(err, "Could not capture the demo snapshot."),
        variant: "destructive",
      });
    },
  });

  return (
    <Card className="p-6">
      <div className="flex items-start gap-3">
        <Snowflake className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Demo Sandbox</h2>
          <p className="text-sm text-muted-foreground">
            Re-capture{" "}
            <a
              href="https://demo.padpulse.io"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-brand hover:underline"
              data-testid="link-demo-subdomain"
            >
              demo.padpulse.io <ExternalLink className="h-3 w-3" />
            </a>{" "}
            from the live source org. Every row is anonymized (names, emails,
            phones, addresses, banking, and credentials are replaced) before it
            loads into the demo account. The source org's real data is never
            modified.
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-md border bg-muted/30 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">Demo login PIN</h3>
              <p className="text-sm text-muted-foreground">
                {pinQuery.isLoading ? (
                  "Loading current PIN…"
                ) : pinQuery.isError ? (
                  "Could not load the current PIN."
                ) : (
                  <>
                    Current PIN:{" "}
                    <span className="font-mono font-semibold tracking-widest" data-testid="text-demo-pin">
                      {pinQuery.data?.pin}
                    </span>{" "}
                    <span className="text-xs text-muted-foreground">
                      ({pinQuery.data?.source === "db" ? "custom override" : "env default"})
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPinInput("");
              setPinDialogOpen(true);
            }}
            data-testid="button-change-demo-pin"
          >
            Change PIN
          </Button>
        </div>
      </div>

      <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Freezing overwrites the current demo data with a fresh snapshot.
            Visitors using the demo will see the new data after their next reset
            or reload.
          </span>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={freeze.isPending}
          data-testid="button-freeze-demo"
          className="gap-2"
        >
          {freeze.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Snowflake className="h-4 w-4" />
          )}
          {freeze.isPending ? "Capturing…" : "Freeze Demo Now"}
        </Button>
        {lastVersion != null && (
          <span className="text-sm text-muted-foreground" data-testid="text-demo-last-version">
            Last captured: snapshot v{lastVersion}
          </span>
        )}
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(v) => {
          if (freeze.isPending) return;
          setConfirmOpen(v);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Freeze the demo sandbox?</AlertDialogTitle>
            <AlertDialogDescription>
              This re-reads the live source org, anonymizes every row, and
              replaces the demo account's data with a fresh snapshot. The source
              org's real rows are not touched. This can take a minute.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={freeze.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-freeze-demo-confirm"
              disabled={freeze.isPending}
              onClick={(e) => {
                e.preventDefault();
                freeze.mutate();
              }}
            >
              {freeze.isPending ? "Capturing…" : "Freeze Now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={pinDialogOpen}
        onOpenChange={(v) => {
          if (updatePin.isPending) return;
          setPinDialogOpen(v);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change demo PIN</DialogTitle>
            <DialogDescription>
              Enter a new 4-digit PIN. It takes effect immediately on the next
              demo login — no redeploy needed.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!/^\d{4}$/.test(pinInput)) {
                toast({
                  title: "Invalid PIN",
                  description: "The PIN must be exactly 4 digits.",
                  variant: "destructive",
                });
                return;
              }
              updatePin.mutate(pinInput);
            }}
            className="space-y-3"
          >
            <div className="space-y-1.5">
              <Label htmlFor="demo-pin-input">New PIN</Label>
              <Input
                id="demo-pin-input"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                placeholder="1234"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="font-mono tracking-widest"
                data-testid="input-demo-pin"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={updatePin.isPending}
                onClick={() => setPinDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updatePin.isPending || pinInput.length !== 4}
                data-testid="button-save-demo-pin"
                className="gap-2"
              >
                {updatePin.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {updatePin.isPending ? "Saving…" : "Save PIN"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
